/* =============================================================================
   VIEWS / ADMIN / ORDER-PROCESS-HISTORY — „Prozessübersicht" (S150)
   =============================================================================
   Druckbares Vorgangsprotokoll pro Order, gespeist aus dem revisionssicheren
   Audit-Log (append-only). Seit S149/phase_112 trägt jede audit_log-Zeile den
   beteiligten Operator → hier wird daraus eine lesbare, chronologische Timeline
   mit Zeiten, Operatoren und Details als „Beweis"-Dokument.

   S215: Anzeige als In-App-MODAL (gemeinsame dark-from-start-Overlay-Factory,
   kein window.open/Popup-Blocker mehr). Das helle Papier-Protokoll lebt in einem
   sandboxed iframe (Style-Isolation + Druck exakt auf den Protokoll-Inhalt via
   iframe-eigenes @media print → 1:1 wie der alte A4-Druck). Dual-Write-Dubletten
   (App + Trigger) werden vor dem Phasen-Bau kollabiert (dedupeAuditRows).

   Aufbau:
     - Pure: PHASE_TYPES, mapAuditToPhase(row, opts) → Step | null
     - Pure: dedupeAuditRows(rows) → Dubletten-freie Zeilen
     - Pure: buildProcessTimeline(rows, order, opts) → { meta, steps, summary }
     - Pure: renderProcessProtocolHtml(model, {embedded}) → HTML-Dokument
     - async: openProcessHistory(orderId) → lädt audit_log + öffnet Modal
     - printProcessHistory() / closeProcessHistory()

   Datenquelle: window.App.storage.audit.listForOrder(order) (gezielte DB-Abfrage,
   greift auch auf abgeschlossene/archivierte Orders, nicht nur den 1000er-Cache).
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { createDarkOverlay } from '../ui/overlay.js';

// -----------------------------------------------------------------------------
// Label-Maps (DE)
// -----------------------------------------------------------------------------
const ROLE_LABEL = {
  owner: 'Inhaber', admin: 'Admin', manager: 'Manager', service: 'Service', staff: 'Aushilfe',
  lager: 'Lager', driver: 'Fahrer'
};
const PAY_LABEL = { cash: 'bar', card: 'Karte', stripe: 'online' };
const SERVICE_LABEL = { 'dine-in': 'Hier essen', takeaway: 'Abholung', delivery: 'Lieferung' };
const CHANNEL_STATUS_LABEL = { pending: 'eingegangen', preparing: 'in Zubereitung', ready: 'fertig' };
const DELIVERY_STATUS_LABEL = {
  assigned: 'Fahrer zugewiesen', out_for_delivery: 'unterwegs', delivered: 'zugestellt', picked_up: 'abgeholt'
};

/** Dot-Farben/Phasentypen für die Timeline. */
export const PHASE_TYPES = ['create', 'kitchen', 'bar', 'delivery', 'pay', 'done', 'storno', 'system'];

function _d(row) { return (row && (row.details || row.detailsJson)) || {}; }
function _opName(row) { return (row && (row.operatorName || row.operator_name)) || ''; }
function _userName(row) { return (row && (row.userName || row.user_name)) || ''; }
function _ts(row) { return (row && (row.timestamp || row.created_at)) || null; }

/** Initialen aus einem Namen ableiten (Fallback, falls keine display_initials). */
export function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Pure: mappt eine Audit-Zeile auf einen Protokoll-Schritt — oder null, wenn die
 * Aktion nicht zum operativen Order-Prozess gehört (Reservierung-Meta, Bon-Archiv,
 * Test-/System-Marker etc. werden ausgeblendet).
 */
export function mapAuditToPhase(row) {
  if (!row || !row.action) return null;
  const d = _d(row);
  const num = (s) => (typeof s === 'number' ? s : (s != null ? Number(s) : null));
  switch (row.action) {
    case 'order.created': {
      // Dual-Write (App camelCase + Trigger snake_case) → beide Schreibweisen
      // lesen, damit die Zeile unabhängig davon vollständig rendert, welche der
      // (per dedupeAuditRows kollabierten) Dubletten übrig bleibt.
      const oNr = d.orderNumber || d.order_number;
      const oNm = d.name || d.customer_name;
      return { type: 'create', label: 'Bestellung angelegt',
        detail: [oNr && `Nr ${oNr}`, oNm && `${oNm}`].filter(Boolean).join(' · ') };
    }
    case 'order.created_from_reservation':
      return { type: 'create', label: 'Bestellung angelegt (aus Reservierung)',
        detail: [d.order_number && `Nr ${d.order_number}`, d.service_type && (SERVICE_LABEL[d.service_type] || d.service_type)].filter(Boolean).join(' · ') };
    case 'order.items_appended':
      return { type: 'create', label: `Weitere Runde angelegt${d.round_no ? ` (Runde ${d.round_no})` : ''}`,
        detail: d.appended ? `${d.appended} Position${d.appended === 1 ? '' : 'en'} ergänzt` : '' };
    case 'order.channel_status_changed':
      return { type: d.channel === 'bar' ? 'bar' : 'kitchen',
        label: `${d.channel === 'bar' ? 'Theke' : 'Küche'} · ${CHANNEL_STATUS_LABEL[d.status] || d.status || ''}`,
        detail: d.master_after ? `Gesamt-Status: ${CHANNEL_STATUS_LABEL[d.master_after] || d.master_after}` : '' };
    case 'order.round_channel_status_changed':
      return { type: d.channel === 'bar' ? 'bar' : 'kitchen',
        label: `${d.channel === 'bar' ? 'Theke' : 'Küche'} · ${CHANNEL_STATUS_LABEL[d.status] || d.status || ''}${d.round_no ? ` (Runde ${d.round_no})` : ''}`,
        detail: '' };
    case 'order.status_changed':
      if (d.to === 'picked_up') return { type: 'done', label: 'Ausgegeben / abgeholt', detail: '' };
      if (d.to === 'confirmed') return { type: 'create', label: 'Bestellung angenommen', detail: '' };
      return null; // ready/preparing → über channel_status abgedeckt; Rest Rauschen
    case 'order.driver_assigned':
      return { type: 'delivery', label: `Fahrer zugewiesen${d.driver_name ? `: ${d.driver_name}` : ''}`, detail: '' };
    case 'delivery.status_changed':
      return { type: 'delivery', label: `Lieferung · ${DELIVERY_STATUS_LABEL[d.new_status] || d.new_status || ''}`, detail: '' };
    case 'order.paid':
      return { type: 'pay', label: `Kassiert · bezahlt (${PAY_LABEL[d.method] || d.method || '—'})`,
        detail: d.invoiceNumber ? `Beleg ${d.invoiceNumber}` : '' };
    case 'order.split_paid':
      return { type: 'pay', label: `Teilzahlung${d.items ? ` (${d.items} Pos.)` : ''}`,
        detail: [d.invoice_number && `Beleg ${d.invoice_number}`, d.fully_paid ? 'vollständig bezahlt' : 'Restbetrag offen'].filter(Boolean).join(' · ') };
    case 'order.cancelled':
      return { type: 'storno', label: 'Storniert', detail: d.by ? `durch ${d.by}` : '' };
    case 'order.correction.created':
      return { type: 'storno', label: 'Korrekturbeleg erstellt',
        detail: [d.correction_invoice_number && `${d.correction_invoice_number}`, d.reason_code && `Grund: ${d.reason_code}`,
          (num(d.gross_cents) != null) && `${(num(d.gross_cents) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`].filter(Boolean).join(' · ') };
    case 'order.schwund':
      return { type: 'storno', label: 'Schwund gebucht (kein Umsatz)',
        detail: [d.reason_category && d.reason_category, (num(d.amount_cents) != null) && `${(num(d.amount_cents) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`].filter(Boolean).join(' · ') };
    case 'order.no_show':
      return { type: 'storno', label: 'No-Show', detail: d.schwund_positions ? `${d.schwund_positions} Position(en) als Schwund` : '' };
    case 'order.partial_cancelled':
      return { type: 'storno', label: 'Teil-Storno',
        detail: [d.qty_cancelled && `${d.qty_cancelled}×`, d.reason && d.reason].filter(Boolean).join(' · ') };
    case 'order.mail-sent':
      return { type: 'system', label: 'Beleg / Mail versendet',
        detail: [d.kind && d.kind, d.to && `→ ${d.to}`].filter(Boolean).join(' · ') };
    default:
      return null;
  }
}

// -----------------------------------------------------------------------------
// Dauer-Formatierung
// -----------------------------------------------------------------------------
export function formatDuration(ms) {
  if (ms == null || !isFinite(ms) || ms < 0) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} Sek.`;
  const m = Math.floor(s / 60), rs = s % 60;
  if (m < 60) return rs ? `${m} Min. ${rs} Sek.` : `${m} Min.`;
  const h = Math.floor(m / 60), rm = m % 60;
  return rm ? `${h} Std. ${rm} Min.` : `${h} Std.`;
}

// -----------------------------------------------------------------------------
// Dedup — audit_log Dual-Write (Memory gg-audit-log-dual-write-dedupe)
// -----------------------------------------------------------------------------
/**
 * Pure: Dedup-Signatur einer Audit-Zeile. `details->>id` (die Order-ID) ist bei
 * ALLEN Aktionen gleich → als alleiniger Schlüssel unbrauchbar. Deshalb
 * `action + details.id + Diskriminator`, wobei der Diskriminator die
 * unterscheidenden Detail-Felder case-insensitiv normalisiert (Status/Kanal/
 * Runde/Methode/kind/…). Dieselbe logische Aktion (App-snake_case + Trigger-
 * camelCase-Dual-Write, plus etwaige Mehrfach-Writes) ergibt EINEN Schlüssel;
 * echte Phasen (z.B. Küche pending/preparing/ready) bleiben getrennt.
 */
function _dedupSig(row) {
  const d = _d(row);
  const id = d.id || d.order_id || d.original_id || d.original_order_id || d.correction_id || '';
  const disc = [
    d.status, d.new_status, d.to, d.channel, d.round_no, d.round,
    d.method, d.kind, d.reason_code, d.correction_invoice_number, d.invoice_number
  ].map(v => (v == null ? '' : String(v).toLowerCase())).join('|');
  return (row && row.action ? row.action : '') + '#' + id + '#' + disc;
}

/**
 * Pure: entfernt Dual-Write-/Mehrfach-Dubletten. Stabil chronologisch — die
 * früheste Zeile pro Ereignis gewinnt. Exportiert (Vitest).
 */
export function dedupeAuditRows(rows) {
  if (!Array.isArray(rows)) return [];
  const sorted = rows.slice().sort((a, b) => (Date.parse(_ts(a)) || 0) - (Date.parse(_ts(b)) || 0));
  const seen = new Set();
  const out = [];
  for (const r of sorted) {
    const k = _dedupSig(r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

// -----------------------------------------------------------------------------
// Timeline-Modell
// -----------------------------------------------------------------------------
/**
 * Pure: baut das vollständige Protokoll-Modell aus den Audit-Zeilen + der Order.
 * @param {Array} rows - audit_log-Zeilen (client-gemappt: operatorName, timestamp, details…)
 * @param {object} order - Order-Header (für Kopf-Meta)
 * @param {{usersById?:Object}} [opts] - users-Lookup für Rolle/Initialen
 * @returns {{ meta, steps, summary }}
 */
export function buildProcessTimeline(rows, order, opts = {}) {
  const usersById = opts.usersById || {};
  const o = order || {};

  // S215 — Dual-Write-Dubletten kollabieren, bevor Phasen gebaut werden
  // (sonst doppelte „Bestellung angelegt"-Zeilen etc.).
  const steps = dedupeAuditRows(rows)
    .map(row => {
      const phase = mapAuditToPhase(row);
      if (!phase) return null;
      const tsRaw = _ts(row);
      const tsMs = tsRaw ? Date.parse(tsRaw) : NaN;
      const opName = _opName(row);
      const u = (row.operatorId || row.operator_id) ? usersById[row.operatorId || row.operator_id] : null;
      const isSystem = !opName;
      return {
        type: PHASE_TYPES.includes(phase.type) ? phase.type : 'system',
        label: phase.label,
        detail: phase.detail || '',
        tsMs: Number.isFinite(tsMs) ? tsMs : null,
        operator: {
          name: opName || 'System',
          role: (u && ROLE_LABEL[u.role]) || '',
          initials: opName ? ((u && u.displayInitials) || initialsFromName(opName)) : 'SY',
          isSystem
        }
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.tsMs || 0) - (b.tsMs || 0));

  // Dauern zwischen aufeinanderfolgenden Schritten
  for (let i = 1; i < steps.length; i++) {
    const prev = steps[i - 1].tsMs, cur = steps[i].tsMs;
    steps[i].durationFromPrev = (prev != null && cur != null) ? formatDuration(cur - prev) : '';
  }

  const first = steps.find(s => s.tsMs != null);
  const last = [...steps].reverse().find(s => s.tsMs != null);
  const totalMs = (first && last && first !== last) ? (last.tsMs - first.tsMs) : null;

  const operatorNames = new Set(steps.filter(s => !s.operator.isSystem).map(s => s.operator.name));
  const hasStorno = steps.some(s => s.type === 'storno');
  const hasPay = steps.some(s => s.type === 'pay');

  const status = (o.status || o._status);
  let resultKind = 'open', resultLabel = 'In Bearbeitung';
  if (hasStorno || status === 'cancelled' || status === 'no_show') {
    resultKind = 'storno';
    resultLabel = status === 'no_show' ? 'No-Show · Schwund' : 'Storniert · Schwund gebucht';
  } else if (hasPay || (o.paymentStatus || o.payment_status) === 'paid') {
    resultKind = 'done';
    resultLabel = 'Abgeschlossen · kassiert';
  }

  const num = o.orderNumber || o.order_number || '—';
  const idTail = String(o.id || '').slice(-6) || 'xxxxxx';
  const svc = o.serviceType || o.service_type;
  let svcText = SERVICE_LABEL[svc] || svc || '—';
  const tableLabel = o.tableId || o.table_id;
  if (svc === 'dine-in' && tableLabel) svcText += ` · Tisch ${tableLabel}`;

  return {
    meta: {
      orderNumber: num,
      invoiceNumber: o.invoiceNumber || o.invoice_number || '',
      date: o.pickupDate || o.pickup_date || '',
      service: svcText,
      paymentMethod: PAY_LABEL[o.paymentMethod || o.payment_method] || (o.paymentMethod || o.payment_method) || '',
      totalGrossCents: (o.totalGrossCents != null ? o.totalGrossCents : o.total_gross_cents) || 0,
      protocolId: `PRT-${num}-${idTail}`,
      isCorrection: !!(o.isCorrection || o.is_correction)
    },
    steps,
    summary: {
      totalDuration: totalMs != null ? formatDuration(totalMs) : '—',
      operatorCount: operatorNames.size,
      phaseCount: steps.length,
      resultKind, resultLabel
    }
  };
}

// -----------------------------------------------------------------------------
// HTML-Rendering (selbst-tragendes Druck-Dokument)
// -----------------------------------------------------------------------------
const DOT_COLOR = {
  create: '#1c2024', kitchen: '#d97706', bar: '#2b6cb0', delivery: '#2b6cb0',
  pay: '#2a9d68', done: '#2a9d68', storno: '#c8412f', system: '#8a93a0'
};

function _timeOfDay(tsMs) {
  if (tsMs == null) return '—';
  try { return new Date(tsMs).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch (_) { return '—'; }
}
function _fmtEUR(cents) {
  return ((Number(cents) || 0) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}
function _fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso + (String(iso).length <= 10 ? 'T00:00:00' : '')).toLocaleDateString('de-DE'); }
  catch (_) { return String(iso); }
}

/**
 * Pure: rendert das vollständige HTML-Dokument (inkl. <style> + Druck-CSS).
 * @param {object} model - aus buildProcessTimeline
 * @param {{restaurantName?:string, createdAtLabel?:string}} [ctx]
 */
export function renderProcessProtocolHtml(model, ctx = {}) {
  const m = model || { meta: {}, steps: [], summary: {} };
  const meta = m.meta || {};
  const sum = m.summary || {};
  const restName = ctx.restaurantName || 'Grill & Green';
  const createdAt = ctx.createdAtLabel || '';
  const statusCls = sum.resultKind === 'storno' ? 'storno' : (sum.resultKind === 'done' ? 'ok' : 'open');
  const statusIcon = sum.resultKind === 'storno' ? '⚠' : (sum.resultKind === 'done' ? '✓' : '•');

  const rows = (m.steps || []).map((s, idx) => {
    const isLast = idx === m.steps.length - 1;
    const op = s.operator || {};
    const who = op.isSystem
      ? `<div class="who system"><span class="badge-ini">SY</span><span class="nm">System</span></div>`
      : `<div class="who"><span class="badge-ini">${htmlEscape(op.initials || '')}</span><span class="nm">${htmlEscape(op.name || '')}</span>${op.role ? `<span class="role">· ${htmlEscape(op.role)}</span>` : ''}</div>`;
    return `
      <div class="tl-row${isLast ? ' tl-row-last' : ''}">
        <div class="tl-time">${htmlEscape(_timeOfDay(s.tsMs))}</div>
        <div class="tl-rail"><span class="tl-dot" style="border-color:${DOT_COLOR[s.type] || DOT_COLOR.system};${s.type === 'storno' ? `background:${DOT_COLOR.storno};` : ''}"></span></div>
        <div class="tl-body">
          <div class="tl-label">${htmlEscape(s.label)}</div>
          ${s.detail ? `<div class="tl-detail">${htmlEscape(s.detail)}</div>` : ''}
          ${who}
          ${s.durationFromPrev ? `<div class="dur">+ ${htmlEscape(s.durationFromPrev)}</div>` : ''}
        </div>
      </div>`;
  }).join('');

  const emptyNote = (m.steps || []).length === 0
    ? `<div class="empty">Noch keine protokollierten Phasen für diesen Vorgang.</div>` : '';

  // S215 — im In-App-Modal (embedded) trägt der Modal-Header „Drucken/Schließen";
  // die eingebettete Print-Bar entfällt (window.print/close gehören zum alten
  // window.open-Fenster). @media print bleibt 1:1 → Druck/PDF unverändert.
  const printBar = ctx.embedded ? '' : `
<div class="print-bar">
  <span>Prozessübersicht · ${htmlEscape(String(meta.orderNumber || ''))}</span>
  <span><button class="pr" onclick="window.print()">🖨 Drucken / als PDF</button> <button class="cl" onclick="window.close()">Schließen</button></span>
</div>`;

  return `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8">
<title>Prozessübersicht ${htmlEscape(String(meta.orderNumber || ''))}</title>
<style>
  :root{--ink:#1c2024;--ink-soft:#5b6470;--ink-muted:#8a93a0;--line:#e3e7ec;--green:#2a9d68;--green-soft:#e7f6ee;--red:#c8412f;--red-soft:#fdecea;--amber-soft:#fff4e0;--amber:#d97706}
  *{box-sizing:border-box}
  body{margin:0;background:#eef1f4;color:var(--ink);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.45}
  .print-bar{background:var(--ink);color:#fff;padding:10px 20px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0}
  .print-bar button{padding:7px 14px;border:0;border-radius:6px;cursor:pointer;font-weight:600;font-size:14px}
  .print-bar .pr{background:var(--green);color:#fff}
  .print-bar .cl{background:transparent;color:#fff;border:1px solid #fff}
  .page{max-width:820px;margin:22px auto;background:#fff;border:1px solid var(--line);border-radius:10px;padding:34px 38px;box-shadow:0 6px 24px rgba(20,30,45,.08)}
  .doc-head{display:flex;justify-content:space-between;align-items:flex-start;gap:18px;border-bottom:2px solid var(--ink);padding-bottom:14px}
  .doc-head .brand{font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:var(--ink-soft);font-weight:700}
  .doc-head h1{font-size:21px;margin:3px 0 2px}
  .doc-head .sub{font-size:12.5px;color:var(--ink-muted)}
  .status{white-space:nowrap;font-size:12.5px;font-weight:700;padding:7px 12px;border-radius:999px;border:1px solid transparent}
  .status.ok{background:var(--green-soft);color:var(--green);border-color:#bfe6d2}
  .status.storno{background:var(--red-soft);color:var(--red);border-color:#f1c9c1}
  .status.open{background:var(--amber-soft);color:var(--amber);border-color:#f1dcb0}
  .meta{display:grid;grid-template-columns:repeat(3,1fr);gap:10px 22px;margin:18px 0 6px}
  .meta .k{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:var(--ink-muted)}
  .meta .v{font-size:14.5px;font-weight:600;margin-top:1px}
  .meta .v.mono{font-variant-numeric:tabular-nums;font-family:"SF Mono",ui-monospace,Menlo,Consolas,monospace;font-size:13.5px}
  .section-label{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--ink-muted);margin:22px 0 6px;font-weight:700}
  .timeline{margin-top:6px}
  .tl-row{position:relative;display:grid;grid-template-columns:74px 26px 1fr}
  .tl-time{font-variant-numeric:tabular-nums;font-family:"SF Mono",ui-monospace,Menlo,Consolas,monospace;font-size:12.5px;color:var(--ink-soft);padding-top:11px;text-align:right;padding-right:10px}
  .tl-rail{position:relative}
  .tl-rail::before{content:"";position:absolute;left:50%;top:0;bottom:0;width:2px;background:var(--line);transform:translateX(-50%)}
  .tl-row:first-child .tl-rail::before{top:14px}
  .tl-row-last .tl-rail::before{bottom:auto;height:14px}
  .tl-dot{position:absolute;left:50%;top:12px;width:13px;height:13px;border-radius:50%;transform:translateX(-50%);background:#fff;border:3px solid var(--ink)}
  .tl-body{padding:7px 0 13px 6px}
  .tl-label{font-weight:600;font-size:14px}
  .tl-detail{color:var(--ink-soft);font-size:12.8px;margin-top:2px}
  .who{display:inline-flex;align-items:center;gap:6px;margin-top:4px}
  .badge-ini{display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:#eef1f4;color:var(--ink);font-size:10.5px;font-weight:800;border:1px solid var(--line)}
  .who .nm{font-size:12.5px;font-weight:600}
  .who .role{font-size:11px;color:var(--ink-muted)}
  .who.system .badge-ini{background:#f4f6f8;color:var(--ink-muted)}
  .dur{display:inline-block;font-size:11px;color:var(--ink-muted);background:#f4f6f8;border:1px solid var(--line);border-radius:999px;padding:1px 8px;margin-top:4px;margin-left:0}
  .empty{padding:30px;text-align:center;color:var(--ink-muted)}
  .summary{display:flex;gap:24px;flex-wrap:wrap;margin-top:18px;padding-top:14px;border-top:1px solid var(--line)}
  .summary .k{color:var(--ink-muted);text-transform:uppercase;letter-spacing:.06em;font-size:10.5px}
  .summary .v{font-weight:700;font-size:16px}
  .doc-foot{margin-top:20px;padding-top:12px;border-top:1px dashed var(--line);color:var(--ink-muted);font-size:11.5px;display:flex;justify-content:space-between;gap:14px;flex-wrap:wrap}
  .doc-foot .pid{font-family:"SF Mono",ui-monospace,Menlo,Consolas,monospace}
  @media print{body{background:#fff}.print-bar{display:none}.page{box-shadow:none;border:none;border-radius:0;margin:0;max-width:none;padding:14mm 16mm}@page{size:A4;margin:0}}
</style></head><body>
${printBar}
<div class="page">
  <div class="doc-head">
    <div>
      <div class="brand">${htmlEscape(restName)}</div>
      <h1>Prozessübersicht — Vorgangsprotokoll</h1>
      <div class="sub">Lückenlose Phasen-Historie aus dem revisionssicheren Audit-Log</div>
    </div>
    <div class="status ${statusCls}">${statusIcon} ${htmlEscape(sum.resultLabel || '')}</div>
  </div>
  <div class="meta">
    <div><div class="k">Bestellung</div><div class="v mono">${htmlEscape(String(meta.orderNumber || '—'))}${meta.isCorrection ? ' (Storno)' : ''}</div></div>
    <div><div class="k">Belegnummer</div><div class="v mono">${htmlEscape(String(meta.invoiceNumber || '—'))}</div></div>
    <div><div class="k">Datum</div><div class="v">${htmlEscape(_fmtDate(meta.date))}</div></div>
    <div><div class="k">Service-Typ</div><div class="v">${htmlEscape(String(meta.service || '—'))}</div></div>
    <div><div class="k">Bezahlart</div><div class="v">${htmlEscape(String(meta.paymentMethod || '—'))}</div></div>
    <div><div class="k">Betrag (brutto)</div><div class="v mono">${htmlEscape(_fmtEUR(meta.totalGrossCents))}</div></div>
  </div>
  <div class="section-label">Prozess-Phasen</div>
  <div class="timeline">${rows}${emptyNote}</div>
  <div class="summary">
    <div><div class="k">Gesamt-Durchlaufzeit</div><div class="v">${htmlEscape(String(sum.totalDuration || '—'))}</div></div>
    <div><div class="k">Beteiligte Operatoren</div><div class="v">${htmlEscape(String(sum.operatorCount != null ? sum.operatorCount : 0))}</div></div>
    <div><div class="k">Phasen erfasst</div><div class="v">${htmlEscape(String(sum.phaseCount != null ? sum.phaseCount : 0))}</div></div>
  </div>
  <div class="doc-foot">
    <div>Erstellt aus dem revisionssicheren Audit-Log (append-only, GoBD §146 AO)${createdAt ? ` · Erstellt am ${htmlEscape(createdAt)}` : ''}</div>
    <div class="pid">Protokoll-ID: ${htmlEscape(String(meta.protocolId || ''))}</div>
  </div>
</div>
</body></html>`;
}

// -----------------------------------------------------------------------------
// Opener (async, DOM/Window)
// -----------------------------------------------------------------------------
export async function openProcessHistory(orderId) {
  const App = (typeof window !== 'undefined') ? window.App : null;
  if (!App || !App.storage) return;
  const state = App.storage._getInternalState ? App.storage._getInternalState() : {};
  const order = (state.orders || []).find(o => o.id === orderId);
  if (!order) {
    if (typeof window.showToast === 'function') window.showToast('Bestellung nicht gefunden', 'error');
    return;
  }

  // users-Lookup für Rolle/Initialen
  const usersById = {};
  (state.users || []).forEach(u => { if (u && u.id) usersById[u.id] = u; });

  // Cache-Filter als robuster Fallback (jüngste 1000 Einträge).
  const cacheRows = (state.auditLog || []).filter(r => {
    const d = (r && r.details) || {};
    return [d.id, d.order_id, d.original_id, d.original_order_id, d.correction_id].includes(orderId);
  });

  let rows = [];
  try {
    if (App.storage.audit && typeof App.storage.audit.listForOrder === 'function') {
      rows = await App.storage.audit.listForOrder(order);
    }
  } catch (e) {
    console.warn('[process-history] listForOrder:', e);
  }
  // Wenn die gezielte DB-Abfrage nichts liefert (älterer Adapter / Filter-Edge),
  // den Cache-Treffer nehmen, damit zumindest jüngere Orders vollständig sind.
  if ((!rows || rows.length === 0) && cacheRows.length) rows = cacheRows;

  const restaurantName = (state.settings && (state.settings.restaurantName || state.settings.name)) || 'Grill & Green';
  const model = buildProcessTimeline(rows, order, { usersById });
  // S215 — embedded: HTML ohne eigene Print-Bar (der Modal-Header druckt/schließt).
  const html = renderProcessProtocolHtml(model, {
    restaurantName,
    createdAtLabel: new Date().toLocaleString('de-DE'),
    embedded: true
  });

  // S215 — In-App-Modal statt window.open (kein Popup-Blocker; FOUC-frei über die
  // gemeinsame dark-from-start-Overlay-Factory). Das Protokoll (helle Papier-Insel)
  // lebt in einem sandboxed iframe → Style-Isolation + Druck exakt auf den
  // Protokoll-Inhalt (iframe-eigenes @media print, 1:1 wie das alte Fenster).
  closeProcessHistory();
  const overlay = createDarkOverlay({
    id: 'proc-history-modal', className: 'proc-history-overlay',
    zIndex: 9400, onClose: closeProcessHistory
  });
  overlay.innerHTML = `
    <div class="proc-modal" onclick="event.stopPropagation()">
      <header class="proc-modal-head">
        <div class="proc-modal-title">🧾 Prozessübersicht · ${htmlEscape(String(model.meta.orderNumber || ''))}</div>
        <div class="proc-modal-actions">
          <button class="btn btn-primary btn-sm" onclick="printProcessHistory()">🖨 Drucken / als PDF</button>
          <button class="order-modal-close" onclick="closeProcessHistory()" aria-label="Schließen">×</button>
        </div>
      </header>
      <div class="proc-modal-body">
        <iframe id="proc-history-frame" class="proc-modal-frame"
                sandbox="allow-same-origin allow-modals" title="Prozessübersicht — Vorgangsprotokoll"></iframe>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const frame = overlay.querySelector('#proc-history-frame');
  if (frame) frame.srcdoc = html;
}

/** S215 — druckt NUR den Protokoll-Inhalt (das iframe-Dokument mit eigenem
 *  @media print) → 1:1 wie die bisherige window.print()-Druckfunktion. */
export function printProcessHistory() {
  const frame = document.getElementById('proc-history-frame');
  try {
    if (frame && frame.contentWindow) {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    }
  } catch (e) {
    console.warn('[process-history] print:', e);
    if (typeof window.showToast === 'function') window.showToast('Druck fehlgeschlagen', 'error');
  }
}

export function closeProcessHistory() {
  const m = document.getElementById('proc-history-modal');
  if (m) {
    if (typeof m._ggDetachKey === 'function') { try { m._ggDetachKey(); } catch (_) {} }
    m.remove();
  }
}

if (typeof window !== 'undefined') {
  window.openProcessHistory = openProcessHistory;
  window.printProcessHistory = printProcessHistory;
  window.closeProcessHistory = closeProcessHistory;
}
